<template>
    <AppLayout title="Manajemen Auditor">
        <template #header>
            <h2 class="font-semibold text-xl text-gray-800 leading-tight">
                Manajemen Auditor
            </h2>
        </template>

        <div class="py-12">
            <div class="max-w-7xl mx-auto sm:px-6 lg:px-8">
                <div class="bg-white overflow-hidden shadow-xl sm:rounded-lg p-6">
                    <!-- Header with Create Button -->
                    <div class="flex justify-between items-center mb-6">
                        <h3 class="text-lg font-medium text-gray-900">
                            Daftar Auditor
                        </h3>
                        <button
                            @click="showCreateModal = true"
                            class="inline-flex items-center px-4 py-2 bg-blue-600 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-blue-700 active:bg-blue-900 focus:outline-none focus:border-blue-900 focus:ring focus:ring-blue-300 disabled:opacity-25 transition"
                        >
                            <i class="pi pi-plus mr-2"></i>
                            Buat Auditor
                        </button>
                    </div>

                    <!-- DataTable -->
                    <DataTable
                        :value="auditors"
                        :loading="loading"
                        :paginator="true"
                        :rows="10"
                        :rowsPerPageOptions="[5, 10, 20, 50]"
                        responsiveLayout="scroll"
                        class="p-datatable-sm"
                    >
                        <Column field="username" header="Username" :sortable="true"></Column>
                        <Column field="fullname" header="Nama Lengkap" :sortable="true"></Column>
                        <Column field="identity" header="Identitas" :sortable="true"></Column>
                        <Column field="email" header="Email" :sortable="true"></Column>
                        <Column field="created_at" header="Dibuat" :sortable="true">
                            <template #body="slotProps">
                                {{ formatDate(slotProps.data.created_at) }}
                            </template>
                        </Column>
                        <Column header="Aksi">
                            <template #body="slotProps">
                                <button
                                    @click="confirmDelete(slotProps.data)"
                                    class="text-red-600 hover:text-red-900"
                                    title="Hapus"
                                >
                                    <i class="pi pi-trash"></i>
                                </button>
                            </template>
                        </Column>
                    </DataTable>
                </div>
            </div>
        </div>

        <!-- Create Auditor Modal -->
        <Dialog
            v-model:visible="showCreateModal"
            :modal="true"
            :closable="true"
            :style="{ width: '500px' }"
            header="Buat Auditor Baru"
        >
            <div class="space-y-4">
                <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">
                        Email Suffix (XXX)
                    </label>
                    <div class="flex items-center">
                        <span class="text-sm text-gray-500 mr-2">auditor_</span>
                        <input
                            v-model="createForm.email_suffix"
                            type="text"
                            class="flex-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200"
                            placeholder="001"
                        />
                        <span class="text-sm text-gray-500 ml-2">@tumbas.app.undip.ac.id</span>
                    </div>
                    <p class="text-xs text-gray-500 mt-1">
                        Hanya huruf, angka, dan underscore
                    </p>
                </div>

                <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">
                        Nama Lengkap
                    </label>
                    <input
                        v-model="createForm.fullname"
                        type="text"
                        class="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200"
                        placeholder="Nama lengkap auditor"
                    />
                </div>

                <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">
                        Identitas
                    </label>
                    <input
                        v-model="createForm.identity"
                        type="text"
                        class="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200"
                        placeholder="NIP/NIK"
                    />
                </div>
            </div>

            <template #footer>
                <button
                    @click="showCreateModal = false"
                    class="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 mr-2"
                >
                    Batal
                </button>
                <button
                    @click="createAuditor"
                    :disabled="creatingAuditor"
                    class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                    <i v-if="creatingAuditor" class="pi pi-spin pi-spinner mr-2"></i>
                    Buat Auditor
                </button>
            </template>
        </Dialog>

        <!-- Password Display Modal -->
        <Dialog
            v-model:visible="showPasswordModal"
            :modal="true"
            :closable="false"
            :style="{ width: '500px' }"
            header="Auditor Berhasil Dibuat"
        >
            <div class="space-y-4">
                <div class="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                    <div class="flex">
                        <div class="flex-shrink-0">
                            <i class="pi pi-exclamation-triangle text-yellow-400"></i>
                        </div>
                        <div class="ml-3">
                            <p class="text-sm text-yellow-700">
                                <strong>PENTING:</strong> Password ini hanya ditampilkan sekali. 
                                Harap salin dan simpan dengan aman.
                            </p>
                        </div>
                    </div>
                </div>

                <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">
                        Username
                    </label>
                    <div class="flex items-center">
                        <input
                            :value="newAuditor.username"
                            type="text"
                            readonly
                            class="flex-1 rounded-md border-gray-300 bg-gray-50 shadow-sm"
                        />
                        <button
                            @click="copyToClipboard(newAuditor.username)"
                            class="ml-2 px-3 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                            title="Salin"
                        >
                            <i class="pi pi-copy"></i>
                        </button>
                    </div>
                </div>

                <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">
                        Password
                    </label>
                    <div class="flex items-center">
                        <input
                            :value="newAuditor.password"
                            type="text"
                            readonly
                            class="flex-1 rounded-md border-gray-300 bg-gray-50 shadow-sm font-mono"
                        />
                        <button
                            @click="copyToClipboard(newAuditor.password)"
                            class="ml-2 px-3 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                            title="Salin"
                        >
                            <i class="pi pi-copy"></i>
                        </button>
                    </div>
                </div>

                <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">
                        Email
                    </label>
                    <input
                        :value="newAuditor.email"
                        type="text"
                        readonly
                        class="w-full rounded-md border-gray-300 bg-gray-50 shadow-sm"
                    />
                </div>
            </div>

            <template #footer>
                <button
                    @click="closePasswordModal"
                    class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                    Saya Sudah Menyalin Password
                </button>
            </template>
        </Dialog>

        <!-- Delete Confirmation Dialog -->
        <Dialog
            v-model:visible="showDeleteModal"
            :modal="true"
            :closable="true"
            :style="{ width: '400px' }"
            header="Konfirmasi Hapus"
        >
            <p class="text-gray-700">
                Apakah Anda yakin ingin menghapus auditor <strong>{{ auditorToDelete?.username }}</strong>?
            </p>

            <template #footer>
                <button
                    @click="showDeleteModal = false"
                    class="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 mr-2"
                >
                    Batal
                </button>
                <button
                    @click="deleteAuditor"
                    :disabled="deletingAuditor"
                    class="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                    <i v-if="deletingAuditor" class="pi pi-spin pi-spinner mr-2"></i>
                    Hapus
                </button>
            </template>
        </Dialog>
    </AppLayout>
</template>

<script>
import { defineComponent } from 'vue';
import AppLayout from '@/Layouts/AppLayout.vue';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Dialog from 'primevue/dialog';
import axios from 'axios';

export default defineComponent({
    components: {
        AppLayout,
        DataTable,
        Column,
        Dialog,
    },

    data() {
        return {
            auditors: [],
            loading: false,
            showCreateModal: false,
            showPasswordModal: false,
            showDeleteModal: false,
            creatingAuditor: false,
            deletingAuditor: false,
            createForm: {
                email_suffix: '',
                fullname: '',
                identity: '',
            },
            newAuditor: {
                username: '',
                password: '',
                email: '',
            },
            auditorToDelete: null,
        };
    },

    mounted() {
        this.loadAuditors();
    },

    methods: {
        async loadAuditors() {
            this.loading = true;
            try {
                const response = await axios.get(this.route('auditor.datatable'));
                this.auditors = response.data.data || [];
            } catch (error) {
                console.error('Error loading auditors:', error);
                alert('Gagal memuat data auditor');
            } finally {
                this.loading = false;
            }
        },

        async createAuditor() {
            if (!this.createForm.email_suffix || !this.createForm.fullname || !this.createForm.identity) {
                alert('Semua field harus diisi');
                return;
            }

            this.creatingAuditor = true;
            try {
                const response = await axios.post(this.route('auditor.store'), this.createForm);
                
                if (response.data.success) {
                    this.newAuditor = response.data.data;
                    this.showCreateModal = false;
                    this.showPasswordModal = true;
                    this.createForm = {
                        email_suffix: '',
                        fullname: '',
                        identity: '',
                    };
                    this.loadAuditors();
                }
            } catch (error) {
                console.error('Error creating auditor:', error);
                const errorMessage = error.response?.data?.error || 'Gagal membuat auditor';
                alert(errorMessage);
            } finally {
                this.creatingAuditor = false;
            }
        },

        confirmDelete(auditor) {
            this.auditorToDelete = auditor;
            this.showDeleteModal = true;
        },

        async deleteAuditor() {
            if (!this.auditorToDelete) return;

            this.deletingAuditor = true;
            try {
                await axios.delete(this.route('auditor.destroy', this.auditorToDelete.id));
                this.showDeleteModal = false;
                this.auditorToDelete = null;
                this.loadAuditors();
            } catch (error) {
                console.error('Error deleting auditor:', error);
                alert('Gagal menghapus auditor');
            } finally {
                this.deletingAuditor = false;
            }
        },

        closePasswordModal() {
            this.showPasswordModal = false;
            this.newAuditor = {
                username: '',
                password: '',
                email: '',
            };
        },

        copyToClipboard(text) {
            navigator.clipboard.writeText(text).then(() => {
                alert('Berhasil disalin ke clipboard');
            }).catch(() => {
                alert('Gagal menyalin ke clipboard');
            });
        },

        formatDate(dateString) {
            if (!dateString) return '-';
            const date = new Date(dateString);
            return date.toLocaleDateString('id-ID', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            });
        },
    },
});
</script>
